import React from 'react';
import PropTypes from 'prop-types';
import SplitPane from 'react-split-pane';
import getContext from 'react-recompose/getContext';
import { AutoSizer } from 'react-virtualized';
import { Scene } from '@graphistry/viz/src/containers/scene';
import { Panel } from '@graphistry/viz/src/containers/panel';
import { Toolbar } from '@graphistry/viz/src/containers/toolbar';
import { InvestigationSearch } from '@graphistry/viz/src/containers/investigationSearch';
import { Session } from '@graphistry/viz/src/containers/session';
import { Settings } from '@graphistry/viz/src/containers/settings';
import { container } from '@graphistry/falcor-react-redux';
import { selectToolbarItem } from '@graphistry/viz/src/actions/toolbar';
import { LegendContainer as Legend } from '@graphistry/viz/src/containers/legend';
import { pageWidthThatToolbarShouldBeVertical } from '@graphistry/viz/src/components/toolbar';
import { InspectorContainer  } from '@graphistry/viz/src/containers/inspector';
import { Collections } from '@graphistry/viz/src/containers/collections';
import { Kepler } from '@graphistry/viz/src/components/kepler/index.tsx';
import { KeplerDropdown } from '@graphistry/viz/src/containers/kepler-dropdown/index.ts';
import { Chat } from '@graphistry/viz/src/containers/chat/index.ts';

import {
    sceneShiftDown,
    sceneWheelMove,
    sceneMouseMove,
    sceneTouchStart,
    onSelectedPointTouchStart,
    onSelectionMaskTouchStart
} from '@graphistry/viz/src/actions/scene';

import { selectLabel } from '@graphistry/viz/src/actions/labels';
import { selectInspectorRow } from '@graphistry/viz/src/actions/inspector';
import { updateCollectionSets } from '@graphistry/viz/src/actions/collections';

import { createLogger } from '@graphistry/common/logger';
const logger = createLogger(__filename);

function renderLeftPanel({ data, isOpen }) {
    return <Panel key="left-panel" side="left" data={data} isOpen={isOpen} />;
}

const fullWidth = { width: `100%`, background: 'transparent', overflow: 'visible' };
const paneStyle = { ...fullWidth, position: `relative` };
export const RIGHT_PANEL_WIDTH = 275;
const rightPanelStyle = {
    ...paneStyle,
    overflow: 'visible',
    pointerEvents: 'none',
    width: RIGHT_PANEL_WIDTH,
    padding: '10px 10px 10px 0',
    position: `absolute`,
    top: 0,
    bottom: 0,
    right: 0,
    zIndex: 200
};

const bottomPanelStyle = {
    ...paneStyle,
    top: 0,
    left: 10,
    width: `auto`,
    marginRight: 20,
    position: `absolute`,
    borderRadius: '3px',
    backgroundColor: '#f9f9f9',
    boxShadow: '0px 0px 18px -2px rgba(0, 0, 0, 0.43)'
};

let View = (
    {
        id,
        data,
        dataset,
        session,
        info = true,
        menu = true,
        search = {},
        selectLabel,
        sceneShiftDown,
        sceneWheelMove,
        sceneMouseMove,
        sceneTouchStart,
        selectToolbarItem,
        selectInspectorRow,
        scene = {},
        legend = {},
        labels = {},
        panels = {},
        toolbar = {},
        onSelectedPointTouchStart,
        onSelectionMaskTouchStart,
        user = {},
        flags = {},
        selectionModifiers = {},
        collections = {},
        columns = [],
        updateCollectionSets,
        kepler = {},
        chat = {}
    } = {}
) => {
    const { left = {}, right = {}, bottom = {} } = panels;
    const isLeftPanelOpen = left && left.id !== undefined;
    const isBottomPanelOpen = bottom && bottom.id !== undefined;
    const isRightPanelOpen = (right && right.visible) || /*(labels && labels.selection) ||*/ (legend && legend.visible);
    const toolbarHeight = !menu || !toolbar?.visible || (window && window.innerWidth < pageWidthThatToolbarShouldBeVertical) ? 0 : 41;
    console.debug({user, flags, dataset, data, scene}, selectionModifiers, '@View');
    return (
        <SplitPane
            split="horizontal"
            allowResize={false}
            style={{ ...fullWidth }}
            paneStyle={paneStyle}
            size={`${toolbarHeight}px`}>
            <Toolbar
                key="toolbar"
                flags={flags}
                menu={menu}
                data={toolbar}
                popoverData={left}
                toolbarHeight={toolbarHeight}
                renderPopover={renderLeftPanel}
                isPopoverOpen={isLeftPanelOpen}
                selectToolbarItem={selectToolbarItem}
                dataset={dataset}
                view={id}
                user={user}
                showCollections={collections.enabled}
                collectionsCount={collections.length}
                showKepler={kepler.enabled}
                keplerMode={kepler.mode}
                keplerDropdownVisible={kepler.dropdownVisible}
            />
            <AutoSizer>
                {({ width: simulationWidth, height: simulationHeight }) => (
                    <SplitPane
                        allowResize={isBottomPanelOpen}
                        minSize={0}
                        paneStyle={paneStyle}
                        pane2Style={!isBottomPanelOpen ? bottomPanelStyle : { ...bottomPanelStyle, marginBottom: 10 }}
                        style={fullWidth}
                        split="horizontal"
                        primary="second"
                        defaultSize={isBottomPanelOpen ? `${(1 - 1 / Math.sqrt(2)) * 100}%` : '0%'}>
                        <AutoSizer>
                            {({ width: sceneWidth, height: sceneHeight }) => (
                                <React.Fragment>
                                    <Scene
                                        key="scene"
                                        data={scene}
                                        datasetLoaded={(session && session.status === 'init' && session.progress === 100) || false}
                                        dataset={dataset}
                                        sceneWidth={sceneWidth - (isRightPanelOpen ? rightPanelStyle.width : 0)}
                                        sceneHeight={sceneHeight}
                                        isKeplerOpen={kepler && kepler.visible}
                                        simulationWidth={simulationWidth}
                                        simulationHeight={simulationHeight}
                                        selectLabel={selectLabel}
                                        toolbarHeight={toolbarHeight - 1}
                                        sceneShiftDown={sceneShiftDown}
                                        sceneWheelMove={sceneWheelMove}
                                        sceneMouseMove={sceneMouseMove}
                                        sceneTouchStart={sceneTouchStart}
                                        selectToolbarItem={selectToolbarItem}
                                        onSelectedPointTouchStart={onSelectedPointTouchStart}
                                        onSelectionMaskTouchStart={onSelectionMaskTouchStart}
                                        style={{
                                            position: `absolute`,
                                            width: simulationWidth,
                                            height: simulationHeight
                                        }}
                                    />
                                    <Session key="session" data={session} />
                                    {/* <InvestigationSearch key="search" data={search}/> */}
                                    <Panel
                                        side="right"
                                        data={right}
                                        key="right-panel"
                                        isOpen={true}
                                        style={rightPanelStyle}>
                                        <InspectorContainer
                                            data={labels}
                                            globalSelection={scene.selection}
                                            onCreateSets={setData => updateCollectionSets({ fromView: true, ...setData })}
                                            collections={{ ...collections, columns: columns || labels?.selection?.columns || [] }}
                                        />
                                        <Legend data={legend} />
                                    </Panel>
                                    {collections.enabled && collections.visible && (
                                        <Collections data={collections} columns={(() => {
                                            const globalCols = columns || [];
                                            const selectionCols = labels?.selection?.columns || [];
                                            const cols = globalCols.length > 0 ? globalCols : selectionCols;
                                            return cols;
                                        })()} />
                                    )}
                                    {kepler && kepler.visible && (
                                        <Kepler
                                            dataset={dataset}
                                            mode={kepler.mode}
                                            onHide={() => selectToolbarItem({ id: 'toggle-kepler-graph', selected: true })}
                                        />
                                    )}
                                    <KeplerDropdown data={kepler} selectToolbarItem={selectToolbarItem} columns={columns} dataset={dataset} />
                                    {flags && flags.flag_talk2graph && <Chat data={chat} />}
                                </React.Fragment>
                            )}
                        </AutoSizer>
                        <AutoSizer style={{ background: 'transparent' }}>
                            {({ width, height = 0 }) => {
                                width = Math.max(width - 0, 0) || 0;
                                height = Math.max(height - 0, 0) || 0;
                                return (
                                    <Panel
                                        side="bottom"
                                        dataset={dataset}
                                        user={user}
                                        flags={flags}
                                        data={bottom}
                                        key="bottom-panel"
                                        colWidth={150}
                                        rowHeight={30}
                                        colHeaderWidth={48}
                                        rowHeaderHeight={32}
                                        height={height}
                                        width={width}
                                        isOpen={isBottomPanelOpen}
                                        style={{ width, height, position: 'relative' }}
                                        selectInspectorRow={selectInspectorRow}
                                    />
                                );
                            }}
                        </AutoSizer>
                    </SplitPane>
                )}
            </AutoSizer>
        </SplitPane>
    );
};

View = getContext({
    info: PropTypes.bool,
    menu: PropTypes.bool
})(View);

View = container({
    renderLoading: true,
    fragment: (
        { search, scene, labels, legend, layout, toolbar, session, filters, exclusions, panels = {}, collections = {}, columns = {}, kepler = {}, chat = {} } = {}
    ) => `{
        id,
        dataset: {
            id,
            entitlements: { paid },
            nodeBindings
        },
        pruneOrphans,
        search: ${InvestigationSearch.fragment(search)},
        scene: ${Scene.fragment(scene)},
        legend: ${Legend.fragment(legend)},
        layout: ${Settings.fragment(layout)},
        toolbar: ${Toolbar.fragment(toolbar)},
        session: ${Session.fragment(session)},
        labels: ${InspectorContainer.fragment(labels)},
        panels: {
            left: ${Panel.fragment(panels.left, { side: 'left' })},
            right: ${Panel.fragment(panels.right, { side: 'right' })},
            bottom: ${Panel.fragment(panels.bottom, { side: 'bottom' })}
        },
        columns: {
            length, [0...${columns?.length ?? 0}]: {
                name, componentType, dataType, identifier, isInternal
            }
        },
        collections: {
            enabled,
            visible,
            length,
            sets: {
                length,
                [0...${collections?.sets?.length ?? 0}]: {
                    id, type, name, description, enabled,
                    node_encoding: {
                        type, color
                    },
                    edge_encoding: {
                        type, color
                    },
                    expr: {
                        type, sets, gfql
                    }
                }
            }
        },
        kepler: {
            enabled,
            visible,
            mode,
            dropdownVisible
        },
        chat: {
            enabled,
            isOpen,
            inputText,
            isLoading,
            currentResponseId,
            runStartTime,
            messages,
            runNodes
        }
    }`,
    dispatchers: {
        selectLabel,
        sceneShiftDown,
        sceneWheelMove,
        sceneMouseMove,
        sceneTouchStart,
        selectToolbarItem,
        selectInspectorRow,
        onSelectedPointTouchStart,
        onSelectionMaskTouchStart,
        updateCollectionSets
    }
})(View);

export { View };
